/**
  * @class meleEditor
  * @description
  *
  * # Purpose
  * Implements `<mele-editor>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-editor></mele-editor>';
  * ```
  * Create element in JavaScript:
  * ```
  * parentEl.appendChildren({
  *   tag: 'mele-editor',
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  *
  */

import meleElement from 'meleElement'
import Quill from 'quill'
import QuillResize from 'quill-resize-module'
Quill.register('modules/resize', QuillResize)

const TOOLBARS = {
  extended: [
    ['bold', 'italic', 'underline', 'strike'], // toggled buttons
    ['blockquote', 'code-block'],

    [{ header: 1 }, { header: 2 }], // custom button values
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
    [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
    [{ direction: 'rtl' }], // text direction

    [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
    [{ header: [1, 2, 3, 4, 5, 6, false] }],

    [{ color: [] }, { background: [] }], // dropdown with defaults from theme
    [{ font: [] }],
    [{ align: [] }],

    ['link'],
    ['clean'], // remove formatting button

  ],
  basic: [
    ['bold', 'italic'], // toggled buttons

    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }], // outdent/indent

    [{ align: [] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['image'],
  ],
}

const PROPERTIES = _.extend({}, {
  placeholder: { type: 'string', description: 'Placeholder, when nothing is visible' },
  toolbar: { type: 'string', description: 'Array of options for toolbar, see: https://quilljs.com/docs/modules/toolbar/', enum: ['basic', 'extended'], default: 'basic' },
  eventHtmlChanged: { type: 'function', description: 'Event fired each time html content within editor is changed' },
}, meleElement._properties)

export default class meleEditor extends meleElement {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    this._body = this.appendChildren({ tag: 'div', class: 'container' })
    setTimeout(()=>{
      this.renderEditor()
    }, 50)
  } // init

  placeholderChanged(value) {
  }

  toolbarChanged(value) {
  }

  getDeltas() {
    const deltas = this._wysiwyg.getContents()
    return deltas.ops
  }

  getHtml() {
    return this._wysiwyg.root.innerHTML
  }

  setHtml(html) {
    if(_.isEmpty(html)) return
    setTimeout(()=>{
      this._wysiwyg.root.innerHTML = ''
      this._wysiwyg.clipboard.dangerouslyPasteHTML(html + '\n', 'silent')
    }, 100)
  }

  setDeltas(deltas) {
    if(!Array.isArray(deltas) || deltas.length === 0) return
    setTimeout(()=>{
      this._wysiwyg.setContents(deltas, 'silent')
    }, 100)
  }

  renderEditor() {
    const me = this

    const options = {
      theme: 'snow',

      modules: {
        toolbar: {
          container: TOOLBARS[this.toolbar] || TOOLBARS.basic,
        },
      },
      placeholder: this.placeholder,
      readOnly: this.readonly,
    }

    this._wysiwyg = new Quill(this._body, options)
    this._wysiwyg.on('text-change', (delta, oldDelta, source)=>{
      if(source === 'user' && typeof me.eventHtmlChanged === 'function') {
        me.eventHtmlChanged(me, me.getHtml())
      }
    })
  }
} // class meleEditor

meleEditor._properties = PROPERTIES
meleEditor.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-editor', meleEditor)
